import {
  type ComponentPropsWithoutRef,
  type ReactNode,
  type RefObject,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import type { Components } from 'react-markdown';
import { Link as RouterLink } from 'react-router-dom';

import { useRouteRef } from '@backstage/core-plugin-api';

import {
  Banner,
  Button,
  Flex,
  Link,
  Text,
  linkClassNames,
} from '@spotify-internal/encore-web';
import {
  DateUtils,
  SearchUtils,
  extractLastNameComponent,
} from '@spotify/confidence-core-react';
import {
  CollaborativeDocumentProvider,
  useCollaborativeDocumentContext,
} from '@spotify/confidence-editor-react';
import {
  ObservationsV1IssueAutonomyLevel,
  type ObservationsV1IssueFeedbackInput,
  ObservationsV1IssueStatus,
  TaskFragmentDoc,
  getTypeOrNull,
} from '@spotify/confidence-graphql';
import {
  ChatInput,
  ChatThreadTail,
  MarkdownContent,
  type UseChatSubmitResult,
  useChat,
  useChatSubmit,
} from '@spotify/plugin-confidence-chat-react';
import { useEnsureFavorite } from '@spotify/plugin-confidence-collections-react';
import {
  IssueFragment,
  ObservationRefFragment,
  TaskFragment,
  TaskState,
  getAutonomyLevelLabel,
  useIssueSuspense,
  useReviewIssue,
  useTasksSuspense,
  useUpdateIssue,
} from '@spotify/plugin-confidence-issues-react';
import { PermissionUtils } from '@spotify/plugin-confidence-permissions-react';

import { EvidenceIssueProvider } from '../../extensions/EvidenceCodeBlock';
import { issueRouteRef } from '../../routes';
import { isIssueSnoozed } from '../../utils/isIssueSnoozed';
import { getIssueChatName } from '../../utils/issueChatUtils';
import { IssueProblemReview } from '../IssueProblemReview';
import {
  IssueAutonomyLevel,
  IssueProperties,
  IssueSurfacesProperty,
} from '../IssueProperties';
import { IssueStateActions } from '../IssueStateActions';
import { TaskInputRequestContent } from '../IssueTasks';
import { ObservationDetailsDialog } from '../ObservationDetailsDialog';
import { ObservationsList } from '../ObservationsList';

import styles from './IssueDetailsPanel.module.css';

export type IssueDetailsPanelProps = {
  focusInputRequest?: boolean;
  issueName: string;
};

export const IssueTldr = ({
  size = 'small',
  tldr,
}: {
  size?: 'base' | 'small';
  tldr?: string | null;
}) => {
  if (!tldr?.trim()) {
    return null;
  }

  return (
    <section className={styles.tldrSection}>
      <Text as="h3" variant="bodyMedium" semanticColor="textSubdued">
        TL;DR
      </Text>
      <MarkdownContent size={size}>{tldr}</MarkdownContent>
    </section>
  );
};

const getSnoozedUntilDate = (issue: IssueFragment): Date | null => {
  if (!issue.snoozedUntilTime) {
    return null;
  }

  const snoozedUntilDate = new Date(issue.snoozedUntilTime);
  return Number.isNaN(snoozedUntilDate.getTime()) ? null : snoozedUntilDate;
};

export const useIssueRequestedTask = (
  issueName: string,
): TaskFragment | undefined => {
  const { tasks } = useTasksSuspense<TaskFragment>({
    filter: `resource:${SearchUtils.escapeQuery(issueName)}`,
    fetchPolicy: 'cache-and-network',
    fragment: TaskFragmentDoc,
    fragmentName: 'Task',
    orderBy: 'createTime',
    pageSize: 50,
  });

  return tasks.find(
    task =>
      task.state === TaskState.WaitingForInput &&
      task.inputRequest &&
      !task.inputResponse,
  );
};

const scrollToRequestedInput = (
  requestedInputRef: RefObject<HTMLDivElement | null>,
): void => {
  requestedInputRef.current?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
};

const IssueNextStep = ({
  canEdit,
  issue,
  task,
  inputRequest,
}: {
  canEdit: boolean;
  issue: IssueFragment;
  task: TaskFragment;
  inputRequest: NonNullable<TaskFragment['inputRequest']>;
}) => {
  const headingId = useId();

  return (
    <Flex
      direction="column"
      gap="tighter-2"
      variant="bordered"
      borderRadius="larger-2"
      padding="looser"
      marginBlockStart="base"
      role="region"
      aria-labelledby={headingId}
    >
      <Flex
        alignItems="baseline"
        justifyContent="space-between"
        gap="base"
        wrap="wrap"
      >
        <Text as="h3" id={headingId} variant="titleExtraSmall">
          What next
        </Text>
        {issue.autonomyLevel && (
          <Text variant="marginal" semanticColor="textSubdued">
            {getAutonomyLevelLabel(issue.autonomyLevel)}
          </Text>
        )}
      </Flex>
      <TaskInputRequestContent
        issueName={issue.name}
        taskName={task.name}
        inputRequest={inputRequest}
        inputResponse={task.inputResponse}
        disabled={!canEdit}
      />
    </Flex>
  );
};

const IssueChatSection = ({
  chatSubmit,
  chatName,
  canEdit,
  onSubmitted,
}: {
  chatSubmit: UseChatSubmitResult;
  chatName: string;
  canEdit: boolean;
  onSubmitted?: () => void;
}) => {
  const [minCreateTime, setMinCreateTime] = useState<string | null>(null);
  const { chat } = useChat({ name: chatName });
  const {
    inputValue,
    isSending,
    attachments,
    handleInputChange,
    handleSubmit,
    addAttachment,
    removeAttachment,
  } = chatSubmit;

  const handleSend = async () => {
    setMinCreateTime(
      currentMinCreateTime => currentMinCreateTime ?? new Date().toISOString(),
    );
    const didSubmit = await handleSubmit();
    if (didSubmit) {
      onSubmitted?.();
    }
  };

  return (
    <div className={styles.issueChatSection}>
      {minCreateTime && (
        <div className={styles.issueChatThread}>
          <ChatThreadTail
            chatName={chatName}
            chatState={chat?.state}
            minCreateTime={minCreateTime}
            limit={50}
            disablePadding
          />
        </div>
      )}
      <ChatInput
        className={styles.issueChatInput}
        value={inputValue}
        onChange={handleInputChange}
        onSubmit={() => {
          void handleSend();
        }}
        disableCaveat
        disableFocusHotkey
        submitDisabled={!canEdit || isSending}
        placeholder="Give follow-up or steering instructions"
        chatName={chatName}
        attachments={attachments}
        onRemoveAttachment={removeAttachment}
        onAttached={addAttachment}
        disabled={!canEdit || isSending}
       data-track-source-anchor="2746204ae0"/>
    </div>
  );
};

const DocumentMarkdownHeading = ({
  children,
}: ComponentPropsWithoutRef<'h1'>) => (
  <Text as="p" variant="bodyMediumBold">
    {children}
  </Text>
);

const documentMarkdownComponents: Components = {
  h1: DocumentMarkdownHeading,
  h2: DocumentMarkdownHeading,
  h3: DocumentMarkdownHeading,
  h4: DocumentMarkdownHeading,
  h5: DocumentMarkdownHeading,
  h6: DocumentMarkdownHeading,
};

const DocumentMarkdownContent = () => {
  const { collaborativeDocument } = useCollaborativeDocumentContext();
  const markdownContent =
    collaborativeDocument.markdown ??
    (typeof collaborativeDocument.content === 'string'
      ? collaborativeDocument.content
      : undefined);

  if (!markdownContent?.trim()) {
    return (
      <Text as="p" variant="bodySmall" semanticColor="textSubdued">
        No content.
      </Text>
    );
  }

  return (
    <div className={styles.documentMarkdown}>
      <MarkdownContent components={documentMarkdownComponents}>
        {markdownContent}
      </MarkdownContent>
    </div>
  );
};

export const IssueProblemStatement = ({
  documentName,
  issueName,
}: {
  documentName: string;
  issueName: string;
}) => (
  <EvidenceIssueProvider issueName={issueName}>
    <CollaborativeDocumentProvider documentName={documentName}>
      <DocumentMarkdownContent />
    </CollaborativeDocumentProvider>
  </EvidenceIssueProvider>
);

export const IssueDetailsOverview = ({
  canEdit,
  focusInputRequest,
  heading,
  issue,
  actions,
}: {
  canEdit: boolean;
  focusInputRequest: boolean;
  heading?: ReactNode;
  issue: IssueFragment;
  actions?: ReactNode;
}) => {
  const problemStatementName = getTypeOrNull(issue.problemStatement)?.name;
  const requestedInputRef = useRef<HTMLDivElement>(null);
  const requestedTask = useIssueRequestedTask(issue.name);
  const isWaitingForInput =
    issue.status === ObservationsV1IssueStatus.WaitingForInput;
  const requestedTaskName = requestedTask?.name ?? null;
  const isWaitingForReview =
    issue.status === ObservationsV1IssueStatus.WaitingForReview;
  const issueRoute = useRouteRef(issueRouteRef);
  const issueId = extractLastNameComponent(issue.name);
  const issuePath = issueId ? issueRoute({ id: issueId }) : undefined;

  useEffect(() => {
    if (focusInputRequest) {
      scrollToRequestedInput(requestedInputRef);
    }
  }, [focusInputRequest, requestedTaskName]);

  return (
    <div className={styles.detailsContent}>
      {heading}
      <IssueTldr tldr={issue.tldr} />
      {isWaitingForInput && requestedTask && (
        <Banner contextual>
          <Flex direction="column" alignItems="start" gap="tighter-4">
            <Text as="p" variant="bodySmallBold">
              Input requested
            </Text>
            <Text as="p" variant="bodySmall">
              This issue needs your input before Confidence can continue.
            </Text>
            <Link
              href="#requested-input"
              variant="bodySmall"
              onClick={event => {
                event.preventDefault();
                scrollToRequestedInput(requestedInputRef);
              }}
             data-track-source-anchor="aaab31cef0">
              Review requested input
            </Link>
          </Flex>
        </Banner>
      )}
      {isWaitingForReview && (
        <Banner contextual colorSet="positiveSubdued">
          <Flex direction="column" alignItems="start" gap="tighter-4">
            <Text as="p" variant="bodySmallBold">
              Review requested
            </Text>
            <Text as="p" variant="bodySmall">
              Confidence is waiting for a review before it can continue.
            </Text>
            {issuePath && (
              <RouterLink
                to={issuePath}
                className={linkClassNames({ variant: 'bodySmall' })}
                data-track-id="link.review-issue"
                data-track-action="navigate"
               data-track-source-anchor="9a0a1a6807">
                Open review
              </RouterLink>
            )}
          </Flex>
        </Banner>
      )}
      {actions}
      <IssueProperties
        canEdit={canEdit}
        issue={issue}
        propertiesClassName={styles.properties}
        trackingContext="issue-details"
      >
        <IssueSurfacesProperty
          canEdit={canEdit}
          issue={issue}
          trackingContext="issue-details"
        />
      </IssueProperties>
      {problemStatementName && (
        <div className={styles.overviewProblemSection}>
          <Text as="h3" variant="titleExtraSmall">
            Problem
          </Text>
          <IssueProblemStatement
            documentName={problemStatementName}
            issueName={issue.name}
          />
        </div>
      )}
      {isWaitingForInput && requestedTask?.inputRequest && (
        <div
          id="requested-input"
          ref={requestedInputRef}
          className={styles.requestedInput}
        >
          <IssueNextStep
            canEdit={canEdit}
            issue={issue}
            task={requestedTask}
            inputRequest={requestedTask.inputRequest}
          />
        </div>
      )}
    </div>
  );
};

export const IssueDetailsContent = ({
  canEdit,
  issue,
  onSteeringSubmitted,
}: {
  canEdit: boolean;
  issue: IssueFragment;
  onSteeringSubmitted?: () => void;
}) => {
  const { updateIssue } = useUpdateIssue({
    onError: 'post-alert',
  });
  const { reviewIssue, isMutating: isReviewing } = useReviewIssue({
    onError: 'post-alert',
  });
  const { ensureFavorite } = useEnsureFavorite();
  const [selectedObservation, setSelectedObservation] =
    useState<ObservationRefFragment | null>(null);

  const snoozedUntilDate = getSnoozedUntilDate(issue);
  const isSnoozed = isIssueSnoozed(issue.snoozedUntilTime);
  const issueChatName = getIssueChatName(issue.name);
  const chatSubmit = useChatSubmit(issueChatName ?? issue.name);
  const problemStatementName = getTypeOrNull(issue.problemStatement)?.name;
  const requestedTask = useIssueRequestedTask(issue.name);
  const requestedInputRef = useRef<HTMLDivElement>(null);
  const isWaitingForInput =
    issue.status === ObservationsV1IssueStatus.WaitingForInput;
  const unsnoozeIssue = (): Promise<IssueFragment> =>
    updateIssue(
      {
        name: issue.name,
        snoozedUntilTime: null,
      },
      'snoozedUntilTime',
    );
  const finishProblemReview = async ({
    autonomyLevel,
    feedback,
  }: {
    autonomyLevel: ObservationsV1IssueAutonomyLevel;
    feedback: ObservationsV1IssueFeedbackInput[];
  }): Promise<boolean> => {
    try {
      await reviewIssue(issue.name, feedback, autonomyLevel);
      void ensureFavorite(issue.name);
      chatSubmit.clearInput();
      onSteeringSubmitted?.();
      return true;
    } catch {
      return false;
    }
  };

  return (
    <div className={styles.detailsContent}>
      <Text as="h2" variant="titleSmall">
        {issue.displayName}
      </Text>
      <IssueTldr size="base" tldr={issue.tldr} />
      {isWaitingForInput && requestedTask?.inputRequest && (
        <Banner contextual colorSet="announcementSubdued">
          <Flex direction="column" alignItems="start" gap="tighter-4">
            <Text as="p" variant="bodySmallBold">
              Input requested
            </Text>
            <Text as="p" variant="bodySmall">
              This issue needs your input before Confidence can continue.
            </Text>
            <Link
              href="#requested-input"
              variant="bodySmall"
              onClick={event => {
                event.preventDefault();
                scrollToRequestedInput(requestedInputRef);
              }}
             data-track-source-anchor="5a50575e65">
              Review requested input
            </Link>
          </Flex>
        </Banner>
      )}
      <div className={styles.detailsTextSections}>
        {isSnoozed && snoozedUntilDate && (
          <Banner contextual>
            <Flex
              alignItems="start"
              justifyContent="space-between"
              gap="tighter-2"
              className={styles.snoozeBannerContent}
            >
              <Flex direction="column" gap="tighter-4">
                <Text as="p" variant="bodySmallBold">
                  Issue snoozed
                </Text>
                <Text as="p" variant="bodySmall">
                  {`This issue is hidden from the open issue list until ${DateUtils.toDateString(
                    snoozedUntilDate,
                    DateUtils.HUMAN_DATE_FORMAT,
                  )}.`}
                </Text>
              </Flex>
              <Button
                variant="secondary-bordered"
                size="small"
                disabled={!canEdit}
                onClick={() => {
                  void unsnoozeIssue();
                }}
               data-track-source-anchor="3ec676d5e7">
                Unsnooze
              </Button>
            </Flex>
          </Banner>
        )}
        <div className={styles.detailsSection}>
          {problemStatementName &&
          issueChatName &&
          issue.status === ObservationsV1IssueStatus.WaitingForReview ? (
            <EvidenceIssueProvider issueName={issue.name}>
              <IssueProblemReview
                canEdit={canEdit}
                documentName={problemStatementName}
                generalFeedback={chatSubmit.inputValue}
                isSubmitting={isReviewing}
                currentAutonomyLevel={issue.autonomyLevel}
                onFinishReview={finishProblemReview}
              />
            </EvidenceIssueProvider>
          ) : (
            <>
              <Text as="h3" variant="titleExtraSmall">
                Problem
              </Text>
              {problemStatementName && (
                <IssueProblemStatement
                  documentName={problemStatementName}
                  issueName={issue.name}
                />
              )}
            </>
          )}
          {isWaitingForInput && requestedTask?.inputRequest && (
            <div
              id="requested-input"
              ref={requestedInputRef}
              className={styles.requestedInput}
            >
              <IssueNextStep
                canEdit={canEdit}
                issue={issue}
                task={requestedTask}
                inputRequest={requestedTask.inputRequest}
              />
            </div>
          )}
          {issueChatName && (
            <IssueChatSection
              chatSubmit={chatSubmit}
              chatName={issueChatName}
              canEdit={canEdit}
              onSubmitted={() => {
                void ensureFavorite(issue.name);
                onSteeringSubmitted?.();
              }}
            />
          )}
        </div>
      </div>
      <div className={styles.detailsSection}>
        <Text as="h3" variant="titleExtraSmall">
          Observations
        </Text>
        <ObservationsList
          issueName={issue.name}
          onPress={setSelectedObservation}
        />
      </div>
      <ObservationDetailsDialog
        observation={selectedObservation}
        onClose={() => setSelectedObservation(null)}
       data-track-source-anchor="8d1a2a0a93;issue-details-content-observation-details-dialog"/>
    </div>
  );
};

export const IssueDetailsControls = ({
  canEdit,
  issue,
}: {
  canEdit: boolean;
  issue: IssueFragment;
}) => (
  <>
    <section className={styles.labeledSection}>
      <Text as="h3" variant="bodyMedium" semanticColor="textSubdued">
        Actions
      </Text>
      <IssueStateActions canEdit={canEdit} issue={issue} />
    </section>
    <section className={styles.labeledSection}>
      <Text as="h3" variant="bodyMedium" semanticColor="textSubdued">
        Autonomy
      </Text>
      <IssueAutonomyLevel
        canEdit={canEdit}
        issue={issue}
        trackingContext="issue-details"
      />
    </section>
  </>
);

export const IssueDetailsPanel = ({
  issueName,
  focusInputRequest = false,
}: IssueDetailsPanelProps) => {
  const { issue } = useIssueSuspense({ name: issueName });
  const canEdit = PermissionUtils.hasRelation(issue, 'can_edit');

  return (
    <aside
      className={styles.detailsPanel}
      aria-label={`${issue.displayName} details`}
    >
      <IssueDetailsOverview
        issue={issue}
        canEdit={canEdit}
        focusInputRequest={focusInputRequest}
        heading={
          <Text as="h2" variant="titleExtraSmall">
            {issue.displayName}
          </Text>
        }
        actions={<IssueDetailsControls canEdit={canEdit} issue={issue} />}
      />
    </aside>
  );
};
